/**
 * Cosmetics catalog — purely visual, no combat impact. Currencies the player
 * sinks into self-expression rather than power. Designed as a Neon-Gem sink
 * (the premium currency) so it doesn't compete with CD/Clean-Code progression.
 *
 * Two categories at launch:
 *  - `avatar_color` — extended palette beyond the free starter colors. Drives
 *    `player.avatarColor` when equipped.
 *  - `title` — text badge shown next to the runner name on profile + leaderboard.
 *
 * Unlock paths: Neon-Gem purchase (Cosmetics-Store) OR achievement reward
 * (some achievements grant a cosmetic directly — see achievements.ts).
 */

export type CosmeticCategory = 'avatar_color' | 'title';

export interface CosmeticDef {
  id: string;
  category: CosmeticCategory;
  nameKey: string;
  /** Price in Neon Gems. 0 = achievement-only (not purchasable directly). */
  neonGemCost: number;
  /** For avatar_color: the actual hex shipped to PlayerAvatar. For title:
   *  the rendered text (English/German handled via nameKey when localised). */
  value: string;
  /** Optional flavour shown in the store tooltip. */
  descriptionKey?: string;
}

// 10 launch cosmetics — 5 avatar-tints + 5 titles. Small but enough to seed
// the store with both purchasable and achievement-locked variants.
export const COSMETICS: Record<string, CosmeticDef> = {
  // ── Avatar Colors (premium tints) ─────────────────────────────
  avatar_neon_magenta: {
    id: 'avatar_neon_magenta', category: 'avatar_color', nameKey: 'cosmetic.avatar_neon_magenta',
    neonGemCost: 100, value: '#ff4af0',
  },
  avatar_arctic_white: {
    id: 'avatar_arctic_white', category: 'avatar_color', nameKey: 'cosmetic.avatar_arctic_white',
    neonGemCost: 100, value: '#f0f7ff',
  },
  avatar_void_purple: {
    id: 'avatar_void_purple', category: 'avatar_color', nameKey: 'cosmetic.avatar_void_purple',
    neonGemCost: 250, value: '#7e3ff2',
  },
  avatar_solar_gold: {
    id: 'avatar_solar_gold', category: 'avatar_color', nameKey: 'cosmetic.avatar_solar_gold',
    neonGemCost: 500, value: '#ffb628',
  },
  avatar_obsidian: {
    // Locked behind achievement (`all_tier_1_clear`), not purchasable.
    id: 'avatar_obsidian', category: 'avatar_color', nameKey: 'cosmetic.avatar_obsidian',
    neonGemCost: 0, value: '#1a1a26',
  },

  // ── Titles (badge text) ────────────────────────────────────────
  title_vault_pioneer: {
    id: 'title_vault_pioneer', category: 'title', nameKey: 'cosmetic.title_vault_pioneer',
    neonGemCost: 0, value: 'Vault Pioneer',
  },
  title_boss_slayer: {
    id: 'title_boss_slayer', category: 'title', nameKey: 'cosmetic.title_boss_slayer',
    neonGemCost: 0, value: 'Boss Slayer',
  },
  title_endless_champion: {
    id: 'title_endless_champion', category: 'title', nameKey: 'cosmetic.title_endless_champion',
    neonGemCost: 0, value: 'Endless Champion',
  },
  title_collector: {
    id: 'title_collector', category: 'title', nameKey: 'cosmetic.title_collector',
    neonGemCost: 200, value: 'Collector',
  },
  title_extractor: {
    id: 'title_extractor', category: 'title', nameKey: 'cosmetic.title_extractor',
    neonGemCost: 200, value: 'Extractor',
  },
};

export function getCosmetic(id: string): CosmeticDef | undefined {
  return COSMETICS[id];
}

/** All cosmetics in a given category — used by the store to render tabs. */
export function cosmeticsByCategory(cat: CosmeticCategory): CosmeticDef[] {
  return Object.values(COSMETICS).filter((c) => c.category === cat);
}

/** Returns the currently-equipped cosmetic id for a category, or null. */
export function equippedCosmetic(
  unlocked: Record<string, { unlockedAt: number; equipped: boolean }> | undefined,
  category: CosmeticCategory,
): string | null {
  if (!unlocked) return null;
  for (const [id, state] of Object.entries(unlocked)) {
    if (!state.equipped) continue;
    const def = COSMETICS[id];
    if (def?.category === category) return id;
  }
  return null;
}
